import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { AccountInfo } from '@azure/msal-browser';

// Tipos para el estado de autenticación
export interface UserProfile {
  id: string;
  name: string;
  email: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  mobilePhone?: string;
  businessPhones?: string[];
  userPrincipalName: string;
}

export interface AuthState {
  // Estado de autenticación
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  
  // Información del usuario
  account: AccountInfo | null;
  userProfile: UserProfile | null;
  userPhoto: string | null;
  
  // Tokens y permisos
  accessToken: string | null;
  idToken: string | null;
  scopes: string[];
  
  // Errores
  error: string | null;
  loginError: string | null;
  
  // Configuración
  loginInProgress: boolean;
  logoutInProgress: boolean;
  
  // Servicios (para compatibilidad con AuthContext)
  authService?: any;
  apiService?: any;
}

export interface AuthActions {
  // Acciones de autenticación
  setAuthenticated: (isAuthenticated: boolean) => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  
  // Gestión de usuario
  setAccount: (account: AccountInfo | null) => void;
  setUserProfile: (profile: UserProfile | null) => void;
  setUserPhoto: (photo: string | null) => void;
  
  // Gestión de tokens
  setTokens: (accessToken: string | null, idToken: string | null) => void;
  setScopes: (scopes: string[]) => void;
  
  // Gestión de errores
  setError: (error: string | null) => void;
  setLoginError: (error: string | null) => void;
  clearErrors: () => void;
  
  // Estados de proceso
  setLoginInProgress: (inProgress: boolean) => void;
  setLogoutInProgress: (inProgress: boolean) => void;
  
  // Acciones compuestas
  loginStart: () => void;
  loginSuccess: (account: AccountInfo, tokens: { accessToken: string; idToken: string }) => void;
  loginFailure: (error: string) => void;
  logoutStart: () => void;
  logoutComplete: () => void;
  
  // Reset
  resetAuth: () => void;
  
  // Servicios (para compatibilidad con AuthContext)
  setAuthService: (service: any) => void;
  setApiService: (service: any) => void;
}

// Estado inicial
const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  isInitialized: false,
  account: null,
  userProfile: null,
  userPhoto: null,
  accessToken: null,
  idToken: null,
  scopes: [],
  error: null,
  loginError: null,
  loginInProgress: false,
  logoutInProgress: false,
  authService: null,
  apiService: null,
};

// Store de autenticación con Zustand
export const useAuthStore = create<AuthState & AuthActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        
        // Acciones básicas
        setAuthenticated: (isAuthenticated: boolean) =>
          set({ isAuthenticated }, false, 'setAuthenticated'),
        
        setLoading: (loading: boolean) =>
          set({ isLoading: loading }, false, 'setLoading'),
        
        setInitialized: (initialized: boolean) =>
          set({ isInitialized: initialized }, false, 'setInitialized'),
        
        setAccount: (account: AccountInfo | null) =>
          set({ account }, false, 'setAccount'),
        
        setUserProfile: (profile: UserProfile | null) =>
          set({ userProfile: profile }, false, 'setUserProfile'),
        
        setUserPhoto: (photo: string | null) =>
          set({ userPhoto: photo }, false, 'setUserPhoto'),
        
        setTokens: (accessToken: string | null, idToken: string | null) =>
          set({ accessToken, idToken }, false, 'setTokens'),
        
        setScopes: (scopes: string[]) =>
          set({ scopes }, false, 'setScopes'),
        
        setError: (error: string | null) =>
          set({ error }, false, 'setError'),
        
        setLoginError: (error: string | null) =>
          set({ loginError: error }, false, 'setLoginError'),
        
        clearErrors: () =>
          set({ error: null, loginError: null }, false, 'clearErrors'),
        
        setLoginInProgress: (inProgress: boolean) =>
          set({ loginInProgress: inProgress }, false, 'setLoginInProgress'),
        
        setLogoutInProgress: (inProgress: boolean) =>
          set({ logoutInProgress: inProgress }, false, 'setLogoutInProgress'),
        
        // Acciones compuestas para flujos completos
        loginStart: () =>
          set({ 
            loginInProgress: true, 
            loginError: null, 
            error: null 
          }, false, 'loginStart'),
        
        loginSuccess: (account: AccountInfo, tokens: { accessToken: string; idToken: string }) =>
          set({ 
            isAuthenticated: true,
            isLoading: false,
            loginInProgress: false,
            account,
            accessToken: tokens.accessToken,
            idToken: tokens.idToken,
            error: null,
            loginError: null
          }, false, 'loginSuccess'),
        
        loginFailure: (error: string) =>
          set({ 
            isAuthenticated: false,
            isLoading: false,
            loginInProgress: false,
            loginError: error,
            account: null,
            accessToken: null,
            idToken: null
          }, false, 'loginFailure'),
        
        logoutStart: () =>
          set({ 
            logoutInProgress: true,
            error: null 
          }, false, 'logoutStart'),
        
        logoutComplete: () =>
          set({ 
            isAuthenticated: false,
            isLoading: false,
            logoutInProgress: false,
            account: null,
            userProfile: null,
            userPhoto: null,
            accessToken: null,
            idToken: null,
            scopes: [],
            error: null,
            loginError: null
          }, false, 'logoutComplete'),
        
        resetAuth: () =>
          set({ ...initialState, isInitialized: true }, false, 'resetAuth'),
        
        // Servicios (para compatibilidad con AuthContext)
        setAuthService: (service: any) =>
          set({ authService: service }, false, 'setAuthService'),
        
        setApiService: (service: any) =>
          set({ apiService: service }, false, 'setApiService'),
      }),
      {
        name: 'auth-store',
        partialize: (state) => ({
          // Persistir datos del usuario y estado de autenticación
          isAuthenticated: state.isAuthenticated,
          scopes: state.scopes,
          userProfile: state.userProfile,
          userPhoto: state.userPhoto,
          account: state.account,
          // NO persistir tokens por seguridad
        }),
      }
    ),
    {
      name: 'auth-store',
    }
  )
);

// Selectores optimizados
export const useAuthState = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const loginInProgress = useAuthStore((state) => state.loginInProgress);
  const logoutInProgress = useAuthStore((state) => state.logoutInProgress);
  
  return {
    isAuthenticated,
    isLoading,
    isInitialized,
    loginInProgress,
    logoutInProgress,
  };
};

export const useUserInfo = () => {
  const account = useAuthStore((state) => state.account);
  const userProfile = useAuthStore((state) => state.userProfile);
  const userPhoto = useAuthStore((state) => state.userPhoto);
  
  return {
    account,
    userProfile,
    userPhoto,
  };
};

export const useAuthTokens = () => {
  const accessToken = useAuthStore((state) => state.accessToken);
  const idToken = useAuthStore((state) => state.idToken);
  const scopes = useAuthStore((state) => state.scopes);
  
  return {
    accessToken,
    idToken,
    scopes,
  };
};

export const useAuthErrors = () => {
  const error = useAuthStore((state) => state.error);
  const loginError = useAuthStore((state) => state.loginError);
  const clearErrors = useAuthStore((state) => state.clearErrors);
  
  return {
    error,
    loginError,
    clearErrors,
  };
};

export const useAuthActions = () => {
  const loginStart = useAuthStore((state) => state.loginStart);
  const loginSuccess = useAuthStore((state) => state.loginSuccess);
  const loginFailure = useAuthStore((state) => state.loginFailure);
  const logoutStart = useAuthStore((state) => state.logoutStart);
  const logoutComplete = useAuthStore((state) => state.logoutComplete);
  const setUserProfile = useAuthStore((state) => state.setUserProfile);
  const setUserPhoto = useAuthStore((state) => state.setUserPhoto);
  const setInitialized = useAuthStore((state) => state.setInitialized);
  const resetAuth = useAuthStore((state) => state.resetAuth);
  
  return {
    loginStart,
    loginSuccess,
    loginFailure,
    logoutStart,
    logoutComplete,
    setUserProfile,
    setUserPhoto,
    setInitialized,
    resetAuth,
  };
};
